import React, { useRef, useState } from "react";
import type { FactorDef, OfferInputs, ScenarioPct, SharePeriod } from "../../types/ambassadors";
import NumberField from "./NumberField";
import StrategicScoreCard from "./StrategicScoreCard";
import { addPeriod, clearManualRates, endChoices, MAX_YEARS, MIN_YEARS, removePeriod, setPeriodEnd, withYears } from "./calc";
import { yearsLabel } from "./format";
import { initials, photoDataUrl } from "./photo";

// The calculator's left column: every input, in the order the offer is built
// (who → how much they matter to us → what their posts are worth → the deal →
// Request's forecast → private notes). Each edit goes through `update` as a function of the latest
// inputs, so an async edit (the photo) never overwrites newer typing.

type Update = (fn: (prev: OfferInputs) => OfferInputs) => void;

interface Props {
  inputs: OfferInputs;
  /** Each period's calculated share in percent, shown until one is typed by hand. */
  calculatedShares: (number | null)[];
  /** The Strategic Influence Score and the multiplier in use. */
  strategic: { score: number | null; multiplier: number | null };
  /** The shared score factor names and weights, and how to save new ones. */
  sharedDefs: FactorDef[];
  onSaveDefs: (defs: FactorDef[]) => Promise<void>;
  update: Update;
}

const DURATIONS = Array.from({ length: MAX_YEARS - MIN_YEARS + 1 }, (_, k) => k + MIN_YEARS);
const SCENARIOS: { key: keyof ScenarioPct; label: string }[] = [
  { key: "conservative", label: "Conservative" },
  { key: "base", label: "Base" },
  { key: "upside", label: "Upside" },
];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="amb-field">
      <span className="amb-field-label">{label}</span>
      {children}
    </label>
  );
}

function PhotoPicker({ photo, name, onPick }: { photo: string; name: string; onPick: (photo: string) => void }) {
  const file = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");
  return (
    <div className="amb-photo">
      <span className="amb-avatar">{photo ? <img src={photo} alt="" /> : initials(name)}</span>
      <div className="amb-photo-actions">
        <button type="button" className="adm-btn-light" onClick={() => file.current?.click()}>
          {photo ? "Change photo" : "Add photo"}
        </button>
        {photo && (
          <button type="button" className="adm-btn-text" onClick={() => onPick("")}>
            Remove
          </button>
        )}
        {error && <span className="error-text">{error}</span>}
      </div>
      <input
        ref={file}
        type="file"
        accept="image/*"
        hidden
        data-testid="amb-photo-input"
        onChange={async (e) => {
          const picked = e.target.files?.[0];
          e.target.value = "";
          if (!picked) return;
          try {
            onPick(await photoDataUrl(picked));
            setError("");
          } catch {
            setError("Couldn't read that image. Try a JPG or PNG.");
          }
        }}
      />
    </div>
  );
}

export default function OfferInputsPanel({ inputs, calculatedShares, strategic, sharedDefs, onSaveDefs, update }: Props) {
  const set = <K extends keyof OfferInputs>(key: K, value: OfferInputs[K]) => update((p) => ({ ...p, [key]: value }));
  const setPeriods = (fn: (ps: SharePeriod[], years: number) => SharePeriod[]) =>
    update((p) => ({ ...p, periods: fn(p.periods, p.years) }));
  const setPeriod = (k: number, patch: Partial<SharePeriod>) =>
    setPeriods((ps) => ps.map((x, j) => (j === k ? { ...x, ...patch } : x)));
  const setFactor = (id: string, patch: { rating?: number; note?: string }) =>
    update((p) => ({ ...p, score: { factors: p.score.factors.map((f) => (f.id === id ? { ...f, ...patch } : f)) } }));
  const setRevenue = (k: number, v: number | null) =>
    update((p) => ({
      ...p,
      revenue: Array.from({ length: Math.max(p.revenue.length, k + 1) }, (_, j) => (j === k ? v : p.revenue[j] ?? null)),
    }));
  const anyManual = inputs.periods.some((p) => p.ratePct !== null);
  // Weights size the calculated shares, so they're needed while any period uses one.
  const anyCalculated = inputs.periods.some((p) => p.ratePct === null);
  const lastPeriod = inputs.periods.length - 1;

  return (
    <div className="amb-inputs">
      <section className="adm-card amb-card">
        <h3 className="adm-section-title">Ambassador</h3>
        <PhotoPicker photo={inputs.photo} name={inputs.djName} onPick={(photo) => set("photo", photo)} />
        <Field label="DJ name">
          <input
            className={`amb-text${inputs.djName.trim() ? "" : " amb-num-missing"}`}
            value={inputs.djName}
            maxLength={120}
            onChange={(e) => set("djName", e.target.value)}
          />
        </Field>
        <div className="amb-grid-2 amb-mt">
          <Field label="Instagram handle">
            <span className="amb-num">
              <span className="amb-num-affix">@</span>
              <input
                aria-label="Instagram handle"
                autoComplete="off"
                value={inputs.instagramHandle.replace(/^@+/, "")}
                maxLength={60}
                onChange={(e) => set("instagramHandle", e.target.value.replace(/^@+/, ""))}
              />
            </span>
          </Field>
          <Field label="Instagram followers">
            <NumberField label="Instagram followers" value={inputs.followers} onChange={(v) => set("followers", v)} />
          </Field>
          <Field label="Average Reel views">
            <NumberField label="Average Reel views" required value={inputs.reelViews} onChange={(v) => set("reelViews", v)} />
          </Field>
          <Field label="Average Story views">
            <NumberField label="Average Story views" required value={inputs.storyViews} onChange={(v) => set("storyViews", v)} />
          </Field>
          <Field label="Instagram Reels per quarter">
            <NumberField label="Instagram Reels per quarter" required value={inputs.reelsPerQuarter} onChange={(v) => set("reelsPerQuarter", v)} />
          </Field>
          <Field label="Instagram Stories per quarter">
            <NumberField label="Instagram Stories per quarter" required value={inputs.storiesPerQuarter} onChange={(v) => set("storiesPerQuarter", v)} />
          </Field>
        </div>
      </section>

      <StrategicScoreCard
        factors={inputs.score.factors}
        score={strategic.score}
        multiplier={strategic.multiplier}
        manual={inputs.strategicMultiplier}
        onRate={(id, rating) => setFactor(id, { rating })}
        onNote={(id, note) => setFactor(id, { note })}
        onManual={(v) => set("strategicMultiplier", v)}
        sharedDefs={sharedDefs}
        onSaveDefs={onSaveDefs}
      />

      <section className="adm-card amb-card">
        <h3 className="adm-section-title">Sponsorship benchmarks</h3>
        <div className="amb-grid-2">
          <Field label="Reel CPM">
            <NumberField label="Reel CPM" prefix="$" required value={inputs.reelCpm} onChange={(v) => set("reelCpm", v)} />
          </Field>
          <Field label="Story CPM">
            <NumberField label="Story CPM" prefix="$" required value={inputs.storyCpm} onChange={(v) => set("storyCpm", v)} />
          </Field>
          <Field label="Early-stage risk premium">
            <NumberField label="Early-stage risk premium" suffix="×" required value={inputs.riskPremium} onChange={(v) => set("riskPremium", v)} />
          </Field>
        </div>
        <p className="amb-note">Illustrative assumptions, not established market prices. CPM = price per 1,000 views.</p>
      </section>

      <section className="adm-card amb-card">
        <h3 className="adm-section-title">Partnership</h3>
        <Field label="Partnership duration">
          <select className="amb-select" value={inputs.years} onChange={(e) => update((p) => withYears(p, Number(e.target.value)))}>
            {DURATIONS.map((y) => (
              <option key={y} value={y}>
                {y} {y === 1 ? "year" : "years"}
              </option>
            ))}
          </select>
        </Field>
        <div className="amb-sched" role="table" aria-label="Revenue-share schedule">
          <div className="amb-sched-row amb-sched-head" role="row">
            <span role="columnheader">Years</span>
            <span role="columnheader">Weight</span>
            <span role="columnheader">Share</span>
            <span />
          </div>
          {inputs.periods.map((p, k) => (
            <div className="amb-sched-row" role="row" key={`${p.start}-${k}`}>
              <span className="amb-sched-years" role="cell">
                {k === lastPeriod ? (
                  p.start === p.end ? p.start : `${p.start} – ${p.end}`
                ) : (
                  <>
                    {p.start} –{" "}
                    <select
                      className="amb-select"
                      aria-label={`Last year of period ${k + 1}`}
                      value={p.end}
                      onChange={(e) => setPeriods((ps, years) => setPeriodEnd(ps, k, Number(e.target.value), years))}
                    >
                      {endChoices(inputs.periods, k, inputs.years).map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </span>
              <span role="cell">
                <NumberField
                  label={`Weight for ${yearsLabel(p.start, p.end)}`}
                  suffix="×"
                  required={anyCalculated}
                  value={p.weight}
                  onChange={(v) => setPeriod(k, { weight: v })}
                />
              </span>
              <span role="cell" className="amb-sched-share">
                <NumberField
                  label={`Share for ${yearsLabel(p.start, p.end)}`}
                  suffix="%"
                  decimals={[3, 3]}
                  calculated={p.ratePct === null}
                  value={p.ratePct ?? calculatedShares[k] ?? null}
                  onChange={(v) => setPeriod(k, { ratePct: v })}
                />
                {p.ratePct !== null && <span className="amb-tag">manual</span>}
              </span>
              <span role="cell">
                {inputs.periods.length > 1 && (
                  <button
                    type="button"
                    className="amb-remove"
                    aria-label={`Remove ${yearsLabel(p.start, p.end)}`}
                    title="Remove this period"
                    onClick={() => setPeriods((ps, years) => removePeriod(ps, k, years))}
                  >
                    ×
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
        <div className="amb-sched-actions">
          <button
            type="button"
            className="adm-btn-light"
            disabled={inputs.periods.length >= inputs.years}
            onClick={() => setPeriods((ps, years) => addPeriod(ps, years))}
          >
            + Add period
          </button>
          {anyManual && (
            <button type="button" className="adm-btn-light" onClick={() => setPeriods(clearManualRates)}>
              Reset to calculated rates
            </button>
          )}
        </div>
        <p className="amb-note">
          Calculated shares are the base rate × each period’s weight. Type a share to set it by hand; clear it to go back to the calculated one.
        </p>
      </section>

      <section className="adm-card amb-card">
        <h3 className="adm-section-title">Request revenue forecast</h3>
        <div className="amb-grid-2">
          {Array.from({ length: inputs.years }, (_, k) => (
            <Field key={k} label={`Year ${k + 1}`}>
              <NumberField
                label={`Year ${k + 1} revenue`}
                prefix="$"
                required
                placeholder="Enter forecast"
                value={inputs.revenue[k] ?? null}
                onChange={(v) => setRevenue(k, v)}
              />
            </Field>
          ))}
        </div>
        <span className="amb-field-label amb-sub">Scenarios, as a % of this forecast</span>
        <div className="amb-grid-3">
          {SCENARIOS.map((s) => (
            <Field key={s.key} label={s.label}>
              <NumberField
                label={`${s.label} scenario`}
                suffix="%"
                required
                value={inputs.scenarioPct[s.key]}
                onChange={(v) => update((p) => ({ ...p, scenarioPct: { ...p.scenarioPct, [s.key]: v } }))}
              />
            </Field>
          ))}
        </div>
        <p className="amb-note">
          Request’s retained platform revenue (the fees we keep), not request volume or talent earnings — the same definition the
          ambassador contract must use. Scenarios never change the share.
        </p>
      </section>

      <section className="adm-card amb-card">
        <h3 className="adm-section-title">Internal notes</h3>
        <textarea
          className="amb-notes"
          aria-label="Internal notes"
          maxLength={5000}
          value={inputs.notes}
          placeholder="Negotiation notes, context, next steps…"
          onChange={(e) => set("notes", e.target.value)}
        />
        <p className="amb-note">Private. Never shown in Present mode.</p>
      </section>
    </div>
  );
}
